import { sumAmounts } from '@dcp/shared'
import type { LocTxnScope } from '@/types/banking'
import type { LocLine, LocReference } from '@/types/loc-availability'

/** Borrowing base, usage and what is left of it, for a reference or one of its lines. */
export interface LocFigures {
  borrowingBaseAmount: number
  locUsage: number
  /** Borrowing Base − LOC Usage. */
  remaining: number
}

/** A row of the Line of Credit Availability table: a reference (pack or facility) or one LOC line of a pack. */
export type LocAvailabilityRow =
  | {
      kind: 'reference'
      reference: LocReference
      /** Sums over the reference's lines (DCP-167 AC.11). */
      figures: LocFigures
      /**
       * The line whose date, note and starting values the row shows: the sole
       * line of a single-line reference, null for a multi-line one ("—").
       */
      detailLine: LocLine | null
      /** Rows of the lines under an expanded reference; empty unless it has two or more lines. */
      lineRows: LocAvailabilityRow[]
    }
  | {
      kind: 'line'
      line: LocLine
      figures: LocFigures
      /** The line itself — same field as on a reference row, so the detail columns read one shape. */
      detailLine: LocLine
    }

/** What the "View bank transactions" pop-up needs to open for one row. */
export interface LocDrill {
  title: string
  /** Which reference's or line's transactions to fetch. */
  scope: LocTxnScope
  /** Starting LOC Usage baseline, folded into the pop-up's Total so it reconciles with the LOC Usage cell. */
  startingBalance: number
  /** Per-facility headings, for a drill spanning several facilities. */
  showGroupHeadings: boolean
  /** The LOC Usage figure shown in the clicked cell, which the pop-up checks its transactions against. */
  expectedTotal: number
}

/** Money is summed in cents (ADR 0007), not with a float `reduce`. */
function figuresOf(lines: readonly LocLine[]): LocFigures {
  const borrowingBaseAmount = sumAmounts(lines.map((line) => line.borrowingBaseAmount))
  const locUsage = sumAmounts(lines.map((line) => line.locUsage))
  return { borrowingBaseAmount, locUsage, remaining: sumAmounts([borrowingBaseAmount, -locUsage]) }
}

function lineRow(line: LocLine): LocAvailabilityRow {
  return { kind: 'line', line, figures: figuresOf([line]), detailLine: line }
}

/**
 * The table's rows: references matching `search` (case-insensitive, on the
 * reference name or any member facility name), packs first and then by name
 * (AC.3). A reference with two or more lines carries one row per line; a
 * single-line reference shows its line's values on its own row instead.
 */
export function locAvailabilityRows(references: readonly LocReference[], search: string): LocAvailabilityRow[] {
  const query = search.trim().toLowerCase()
  return references
    .filter(
      (reference) =>
        !query ||
        reference.name.toLowerCase().includes(query) ||
        reference.facilities.some((facility) => facility.toLowerCase().includes(query)),
    )
    .sort((a, b) => {
      if (a.type !== b.type) return a.type === 'pack' ? -1 : 1
      return a.name.localeCompare(b.name)
    })
    .map((reference) => {
      const multiLine = reference.lines.length > 1
      return {
        kind: 'reference',
        reference,
        figures: figuresOf(reference.lines),
        detailLine: multiLine ? null : (reference.lines[0] ?? null),
        lineRows: multiLine ? reference.lines.map(lineRow) : [],
      }
    })
}

/**
 * The drill-down a row's "View bank transactions" button opens.
 *
 * A reference scopes by its OWN id and the RPC gathers every contributing line
 * server-side. That also keeps a fabricated id out of the request:
 * `useLocAvailability` synthesizes a line with `id: ''` for a facility with no
 * `loc_lines` row yet. Line rows exist only under multi-line references, whose
 * lines are always real `loc_lines` rows, so a line scope never carries ''.
 */
export function locDrillFor(row: LocAvailabilityRow): LocDrill {
  if (row.kind === 'line') {
    const { line } = row
    return {
      title: line.facilities.join(', ') || line.name,
      scope: { kind: 'line', id: line.id },
      startingBalance: line.startingLocUsageAmount ?? 0,
      // A single-facility line's title IS that facility, so no heading.
      showGroupHeadings: line.facilities.length > 1,
      expectedTotal: row.figures.locUsage,
    }
  }
  const { reference } = row
  return {
    title: reference.name,
    scope: { kind: reference.type, id: reference.id },
    startingBalance: sumAmounts(reference.lines.map((line) => line.startingLocUsageAmount ?? 0)),
    // A pack's title is the pack name, so its facilities get headings even for a single member.
    showGroupHeadings: reference.type === 'pack',
    expectedTotal: row.figures.locUsage,
  }
}
